import { SectionCard } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { Copy, ExternalLink } from 'lucide-react'
import type { ReactNode } from 'react'
import { notification } from '@/notifications/notification-store'
import { type OptionCard, OptionCards } from './OptionCards'
import type { SourceMaterialCode } from './stage-helpers'

/** The PM's answer to whether the order comes with a distribution package to review. */
export enum DistributionPackageChoice {
	EXISTING = 'existing',
	NONE = 'none',
}

const CHOICES: OptionCard<DistributionPackageChoice>[] = [
	{ value: DistributionPackageChoice.EXISTING, title: 'Yes, existing Distribution Package' },
	{ value: DistributionPackageChoice.NONE, title: 'There is no Distribution Package' },
]

export interface SourceMaterialsSectionProps {
	/** The order's original Veeva codes, each with where Open in Veeva takes it. */
	codes: SourceMaterialCode[]
	/** Where Open in Veeva goes when the order has no code; null when there is nowhere. */
	fallbackUrl: string | null
	/** The PM's package choice; absent for anyone who is not asked it. */
	packageChoice?: { value: DistributionPackageChoice | null; onChange: (value: DistributionPackageChoice) => void }
	/** The package files uploaded outside Veeva, and the drop zone when they can be added; absent when there are none. */
	uploads?: ReactNode
}

function CopyCodeButton({ code }: { code: string }) {
	const copy = () =>
		navigator.clipboard.writeText(code).then(
			() => notification.success(`${code} copied.`),
			() => notification.error(`Could not copy ${code}.`),
		)
	return (
		<Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={`Copy ${code}`} onPress={() => void copy()}>
			<Copy className="h-4 w-4 text-ds-primary" />
		</Button>
	)
}

function OpenInVeeva({ url, code }: { url: string; code: string | null }) {
	return (
		<a
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			aria-label={code ? `Open ${code} in Veeva` : 'Open in Veeva'}
			className="inline-flex items-center gap-1.5 text-sm font-medium text-ds-primary hover:underline"
		>
			Open in Veeva
			<ExternalLink className="h-4 w-4" aria-hidden />
		</a>
	)
}

function PackageRow({ code, url }: { code: string | null; url: string }) {
	return (
		<li className="flex flex-wrap items-center justify-between gap-3 rounded-ds-md border border-ds-border px-4 py-3">
			<div>
				<p className="text-xs text-ds-text-muted">Veeva Distribution Package code</p>
				{code ? (
					<p className="mt-1 inline-flex items-center gap-1 text-sm text-ds-text">
						{code}
						<CopyCodeButton code={code} />
					</p>
				) : (
					<p className="mt-1 text-sm text-ds-text-subtle">No code</p>
				)}
			</div>
			<OpenInVeeva url={url} code={code} />
		</li>
	)
}

/**
 * Where the designer finds the material to check: the order's original Veeva codes, a way into
 * Veeva for each, and any package files uploaded outside Veeva. The PM first says whether there
 * is a package at all; without one there is nothing to show.
 */
export function SourceMaterialsSection({ codes, fallbackUrl, packageChoice, uploads }: SourceMaterialsSectionProps) {
	const rows: { code: string | null; url: string }[] = codes.length
		? codes
		: fallbackUrl
			? [{ code: null, url: fallbackUrl }]
			: []
	const showsPackage = packageChoice?.value !== DistributionPackageChoice.NONE
	return (
		<SectionCard
			title="Stage 1 - Source materials"
			collapsible
			description={packageChoice ? 'Is there an existing Distribution Package for this Work Order?' : undefined}
		>
			<div className="space-y-4">
				{packageChoice ? (
					<OptionCards
						label="Existing Distribution Package"
						options={CHOICES}
						value={packageChoice.value}
						onChange={packageChoice.onChange}
					/>
				) : null}
				{showsPackage ? uploads : null}
				{showsPackage && rows.length ? (
					<ul className="space-y-2">
						{rows.map((row) => (
							<PackageRow key={row.code ?? row.url} code={row.code} url={row.url} />
						))}
					</ul>
				) : null}
				{!packageChoice && !uploads && !rows.length ? (
					<p className="text-sm text-ds-text-subtle">No Veeva package is linked to this order.</p>
				) : null}
			</div>
		</SectionCard>
	)
}
